import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ContentCopy,
  ExpandMore,
  Key,
  Link as LinkIcon,
  DeleteOutline,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { useSnackBar } from '../../../hooks/useSnackBar';
import {
  useZapierApiKeyMutations,
  useZapierApiKeys,
} from '../../../hooks/queries/useZapierApiKeys';
import {
  ZapierApiKeyCreated,
  ZapierApiKeyMeta,
} from '../../../services/integrationsService';
import StyledButton from '../../shared/Button/StyledButton';
import Modal from '../../shared/Modal/Modal';
import { TranslationKey } from '../../../types/i18n';
import zapierLogo from '../../../assets/zapier_logo.png';

const ZAPIER_EXPLORE_URL = 'https://zapier.com/apps';
const MAX_ZAPIER_API_KEYS = 5;

const SETUP_STEPS: TranslationKey[] = [
  'Create an API key below and copy it — shown only once',
  'In Zapier, add Elora and paste the key when asked',
  'Trigger: New contact collected → your CRM or app',
  'Lost the key? Revoke it here and create a new one',
];

const CRM_EXAMPLES: { title: string; mappingKey: TranslationKey }[] = [
  {
    title: 'Odoo',
    mappingKey:
      'Action: Create Lead — map name, email, phone, company, summary',
  },
  {
    title: 'Zoho CRM',
    mappingKey:
      'Action: Create/Update Lead or Contact — map name, email, phone, company, summary',
  },
  {
    title: 'Ezekia',
    mappingKey:
      'Action: Create Person — map name, email, phone, company, summary',
  },
];

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const ZapierIntegration: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const showSnackBar = useSnackBar();
  const { data, isLoading, isError, isFetching, refetch } =
    useZapierApiKeys(true);
  const { createKey, revokeKey } = useZapierApiKeyMutations();
  const [plaintextKey, setPlaintextKey] = useState<string | null>(null);

  const keys = (data?.message?.keys || []) as ZapierApiKeyMeta[];
  const atKeyLimit = keys.length >= MAX_ZAPIER_API_KEYS;
  const canCreate = !isError && !atKeyLimit && !createKey.isPending;

  const handleCreate = async () => {
    try {
      const response = await createKey.mutateAsync();
      const created = response.message as ZapierApiKeyCreated;
      if (!created?.api_key) {
        showSnackBar(t('Failed to create Zapier API key'), 'error');
        return;
      }
      setPlaintextKey(created.api_key);
      showSnackBar(t('Zapier API key created'), 'success');
    } catch (error: unknown) {
      showSnackBar(
        getErrorMessage(error, t('Failed to create Zapier API key')),
        'error'
      );
    }
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showSnackBar(t('Copied to clipboard'), 'success');
    } catch {
      showSnackBar(t('Failed to copy to clipboard'), 'error');
    }
  };

  const handleRevoke = async (keyId: string) => {
    try {
      await revokeKey.mutateAsync(keyId);
      if (plaintextKey?.includes(keyId)) {
        setPlaintextKey(null);
      }
      showSnackBar(t('Zapier API key revoked'), 'success');
    } catch (error: unknown) {
      showSnackBar(
        getErrorMessage(error, t('Failed to revoke Zapier API key')),
        'error'
      );
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&:last-child': { pb: 2 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            component="img"
            src={zapierLogo}
            alt="Zapier"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                component="div"
              >
                Zapier
              </Typography>
              {!isError &&
                (keys.length > 0 ? (
                  <Chip
                    icon={<Key />}
                    label={t('{{count}} API key', { count: keys.length })}
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip label={t('No API key')} color="default" size="small" />
                ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t('Automate CRM and apps when contacts are collected')}
            </Typography>
          </Box>
        </Stack>

        {isError && (
          <Alert
            severity="error"
            action={
              <StyledButton
                variant="text"
                color="inherit"
                size="small"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
              >
                {t('Retry')}
              </StyledButton>
            }
          >
            {t('Failed to load Zapier API keys.')}
          </Alert>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <StyledButton
            variant="contained"
            color="primary"
            size="small"
            onClick={handleCreate}
            disabled={!canCreate}
            startIcon={<Key />}
          >
            {createKey.isPending
              ? t('Creating...')
              : t('Create Zapier API key')}
          </StyledButton>
          <StyledButton
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<LinkIcon />}
            onClick={() =>
              window.open(ZAPIER_EXPLORE_URL, '_blank', 'noopener,noreferrer')
            }
          >
            {t('Open Zapier')}
          </StyledButton>
        </Stack>

        {plaintextKey && (
          <Alert severity="warning" sx={{ py: 1 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              {t('Copy this key now — it will not be shown again')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                component="code"
                sx={{
                  wordBreak: 'break-all',
                  flex: 1,
                  fontFamily: 'monospace',
                }}
              >
                {plaintextKey}
              </Typography>
              <StyledButton
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<ContentCopy />}
                onClick={() => handleCopy(plaintextKey)}
              >
                {t('Copy')}
              </StyledButton>
            </Stack>
          </Alert>
        )}

        {keys.length > 0 && (
          <Stack spacing={1}>
            {keys.map(key => (
              <Stack
                key={key.id}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.default',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    fontWeight="medium"
                  >
                    {key.prefix}…
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Created on')}{' '}
                    {new Date(key.created_at).toLocaleString()}
                  </Typography>
                </Box>
                <Modal
                  callback={() => handleRevoke(key.id)}
                  mainPromptText={t('Revoke this Zapier API key?')}
                  description={t(
                    'Any Zaps using this key will stop working immediately. This cannot be undone.'
                  )}
                  confirmText={t('Revoke')}
                  denyText={t('Cancel')}
                  openModalButtonText={t('Revoke')}
                  openModalButtonColor="error"
                  openModalButtonVariant="outlined"
                  openModalIcon={<DeleteOutline />}
                  buttonDisabled={revokeKey.isPending}
                />
              </Stack>
            ))}
          </Stack>
        )}

        <Box
          sx={{
            mt: 'auto',
            '& .MuiAccordion-root': {
              boxShadow: 'none',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 },
            },
            '& .MuiAccordionSummary-root': {
              minHeight: 40,
              '&.Mui-expanded': { minHeight: 40 },
            },
            '& .MuiAccordionSummary-content': {
              my: 1,
              '&.Mui-expanded': { my: 1 },
            },
            '& .MuiAccordionDetails-root': { pt: 0, pb: 1.5 },
          }}
        >
          <Stack spacing={1}>
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" fontWeight="medium">
                  {t('Setup')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense sx={{ py: 0 }}>
                  {SETUP_STEPS.map((stepKey, index) => (
                    <ListItem key={stepKey} sx={{ px: 0, py: 0.25 }}>
                      <ListItemText
                        primary={`${index + 1}. ${t(stepKey)}`}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.secondary',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" fontWeight="medium">
                  {t('CRM examples')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 1 }}
                >
                  {t(
                    'Same Elora trigger for every CRM — only the action and field mapping change'
                  )}
                </Typography>
                <Stack spacing={1}>
                  {CRM_EXAMPLES.map(example => (
                    <Box key={example.title}>
                      <Typography variant="body2" fontWeight="medium">
                        {example.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(example.mappingKey)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ZapierIntegration;
